import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSitemap as SitemapIcon } from 'react-icons/fa';
import CloseIcon from '@mui/icons-material/Close';
import type { DialogProps } from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import type { components } from 'src/types/allApiTypes.gen';
import type { ModuleOrPlanActionOrgUnit, OrgUnit } from 'src/types/orgUnit';
import type { FilterGroups } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import OrgTree from 'src/components/common/OrgTree';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import {
  CHECKBOX_MODE,
  ORG_UNIT_FILTER_NAME,
} from 'src/components/common/OrgTree/types';

import { styles } from './index.styles';

export const ORG_UNIT_MODAL_TEST_ID = 'org-unit-modal';

export interface OnSelectProps {
  orgUnit: OrgUnit | null | undefined;
  filterGroups: FilterGroups;
}

export interface OrgUnitModalProps
  extends Omit<DialogProps, 'onSelect' | 'open'> {
  checkboxMode?: number;
  disallowSelectionFor?: number[];
  moduleId: number;
  onClose: () => void;
  onSelect: (onSelectProps: OnSelectProps) => void;
  orgUnits?: ModuleOrPlanActionOrgUnit[];
  showModal: boolean;
  subTitle?: string;
  useAdminUserPermissions?: boolean;
}

const OrgUnitModal: FC<OrgUnitModalProps> = ({
  checkboxMode = CHECKBOX_MODE.NONE,
  disallowSelectionFor,
  moduleId,
  onClose,
  onSelect,
  orgUnits,
  showModal,
  subTitle,
  useAdminUserPermissions,
  sx = [],
  ...props
}) => {
  const { t } = useTranslation(['common', 'admin']);
  const { activeUserPermissionsInCurrentSurvey, adminUserPermissions } =
    useUserPermissions();
  const initialOrgUnits = useRef<ModuleOrPlanActionOrgUnit[]>();
  const { reset, filterGroups } = useFilterContext();
  const isMultiCheckboxMode = checkboxMode === CHECKBOX_MODE.MULTI;
  const orgTreeState = useOrgTreeState();
  const currentOrgUnit = orgTreeState.latestSelectedOrgUnit;

  const disallowSelected = useMemo(
    () => disallowSelectionFor?.includes(currentOrgUnit?.id || -1),
    [disallowSelectionFor, currentOrgUnit],
  );

  const disableConfirmButtonInCheckboxMode = useMemo(
    () => isMultiCheckboxMode && !filterGroups.orgUnit?.length,
    [isMultiCheckboxMode, filterGroups],
  );

  const disableConfirmButtonWithoutCheckboxMode = useMemo(
    () => !isMultiCheckboxMode && (disallowSelected || !currentOrgUnit),
    [isMultiCheckboxMode, disallowSelected, currentOrgUnit],
  );

  /**
   * Get initial OrgUnits from the user permission.
   */
  const initialOrgUnitsFromPermission = useMemo(() => {
    const userPermissions = useAdminUserPermissions
      ? adminUserPermissions
      : activeUserPermissionsInCurrentSurvey;
    const permission = userPermissions.find(
      ({ module: { id } }) => id === moduleId,
    );

    return permission?.org_units || [];
  }, [activeUserPermissionsInCurrentSurvey, moduleId]);

  /**
   * Handle the confirmation.
   */
  const handleConfirm = useCallback(() => {
    onSelect({ orgUnit: currentOrgUnit, filterGroups });
    onClose();
  }, [currentOrgUnit, filterGroups, onClose, onSelect]);

  /**
   * Check if orgUnits are passed in the Component.
   * If yes, use the child orgUnits as initialOrgUnits.
   * If not the orgTree should be initialized with the root OrgUnit of the user permissions.
   */
  useEffect(() => {
    if (orgUnits) {
      initialOrgUnits.current = orgUnits;
    } else {
      initialOrgUnits.current =
        initialOrgUnitsFromPermission as components['schemas']['ModulePermissionOrgUnitPolymorphic'][];
    }
  }, [orgUnits, initialOrgUnitsFromPermission]);

  /**
   * Reset filterContext
   */
  const resetFilter = useCallback(() => {
    reset({
      defaultFilterGroups: {
        [ORG_UNIT_FILTER_NAME]: [],
      },
      filterSpecs: [
        {
          name: ORG_UNIT_FILTER_NAME,
          multiple: true,
          required: false,
        },
        {
          name: new RegExp('.*'),
          multiple: true,
          required: false,
        },
      ],
    });
  }, [reset]);

  useEffect(() => {
    if (isMultiCheckboxMode) {
      resetFilter();
    }
  }, [showModal]);

  return (
    <Dialog
      data-test-id={ORG_UNIT_MODAL_TEST_ID}
      fullWidth
      onClose={onClose}
      open={showModal}
      sx={[styles.root, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    >
      <DialogTitle
        component="div"
        display="flex"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h2" sx={{ display: 'flex' }}>
          <Box component="span" sx={{ mr: 2 }}>
            <SitemapIcon size={24} />
          </Box>
          {t('admin:Permission.SelectOrgUnit')}
        </Typography>

        <IconButton data-test-id="dialog-close-button" onClick={onClose}>
          <CloseIcon color="primary" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {subTitle && <Typography sx={{ mb: 3 }}>{subTitle}</Typography>}

        <OrgTree
          orgTreeState={orgTreeState}
          checkboxMode={checkboxMode}
          initialOrgUnits={initialOrgUnits.current}
          moduleId={moduleId}
        />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button fullWidth onClick={onClose} sx={{ mr: 2 }} variant="cta">
            {t('cancel')}
          </Button>

          <Button
            className="OrgUnitModal-confirmButton"
            disabled={
              disableConfirmButtonInCheckboxMode ||
              disableConfirmButtonWithoutCheckboxMode
            }
            fullWidth
            onClick={handleConfirm}
            variant="ctaAlt"
          >
            {t('confirm')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OrgUnitModal;
